import type { PlatformConnectionRow, PlatformType } from '@/features/platform/types'
import { constructWorkflowMaxAuthUrl as constructWorkflowMaxAuthUrlBase, getBrwfmRedirectUrl } from '@/lib/brwfm'

const xpmBaseByEnv: Record<string, string> = {
  production: 'https://sso-xpm.dashboardinsights.com',
  devtest: 'https://xpm-sso-dev.dashboardinsights.com',
  dev: 'https://xpm-sso-dev.dashboardinsights.com',
  staging: 'https://xpm-sso-staging.dashboardinsights.com',
  prod: 'https://xpm-sso-prod.dashboardinsights.com',
  portal: 'https://xpm-sso-portal.dashboardinsights.com',
}

export function formatPlatformDateTime(value: string) {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function constructWorkflowMaxAuthUrl(redirectUrl?: string) {
  return constructWorkflowMaxAuthUrlBase(redirectUrl)
}

export function myobClientIdForEnv(envName: string) {
  const configured = import.meta.env['VITE_MYOB_CLIENT_ID'] as string | undefined
  if (configured) return configured
  if (envName === 'production' || envName === 'portal') return 'e92f0317-aae9-49ef-b289-ad4d60162150'
  if (envName === 'staging') return '4c5146a6-492d-4244-979c-3fcb1a997773'
  return '4e58faa9-2d5e-431a-abe1-b26e93b9f33a'
}

export function quickBooksClientIdForEnv(envName: string) {
  const configured = import.meta.env['VITE_QUICKBOOKS_CLIENT_ID'] as string | undefined
  if (configured) return configured
  if (envName === 'production' || envName === 'portal' || envName === 'staging') return 'ABz315D7CYPEG2VHS3MFkrwbFcrBJImBhQIifSYm8teiu582lg'
  return 'ABg4B9CLGrd0ZGwhpajNmpA9BabR9FhaK0YhZYqPwY1W2laY9P'
}

export function getXeroSingleSsoBaseUrl(envName: string): string {
  if (envName === 'production') return 'https://sso-xero.dashboardinsights.com'
  if (envName === 'devtest') return 'https://xero-sso-dev.dashboardinsights.com'
  if (envName === 'staging') return 'https://xero-sso-staging.dashboardinsights.com'
  if (envName === 'prod') return 'https://xero-sso-prod.dashboardinsights.com'
  if (envName === 'portal') return 'https://xero-sso-portal.dashboardinsights.com'
  return 'https://xero-sso-dev.dashboardinsights.com'
}

export function getXeroBulkSsoBaseUrl(envName: string): string {
  if (envName === 'production' || envName === 'prod') return 'https://xero-bulk-sso-prod.dashboardinsights.com/'
  if (envName === 'portal') return 'https://xero-bulk-sso-portal.dashboardinsights.com/'
  if (envName === 'test' || envName === 'dev' || envName === 'devtest') return 'https://xero-bulk-sso-dev.dashboardinsights.com/'
  if (envName === 'staging') return 'https://xero-bulk-sso-staging.dashboardinsights.com/'
  return 'https://xero-bulk-sso-dev.dashboardinsights.com/'
}

export function getXeroBulkSsoUrl(clientId: string, userId: string, envName: string): string {
  const base = getXeroBulkSsoBaseUrl(envName)
  const params = new URLSearchParams({ clientId, userId })
  return `${base}?${params.toString()}`
}

export function showAddXeroConnection(envName: string): boolean {
  return ['prod', 'production', 'portal', 'staging'].includes(envName)
}

export function constructAuthUrl(
  type: PlatformType,
  platformId: string,
  envName: string,
  origin: string,
  oauthState?: string,
) {
  const myobClientId = myobClientIdForEnv(envName)
  const quickBooksClientId = quickBooksClientIdForEnv(envName)

  if (type === 'Xero') {
    const base = getXeroSingleSsoBaseUrl(envName)
    return `${base}?platformId=${encodeURIComponent(platformId)}`
  }

  let baseUrl = ''
  if (type === 'XPM') {
    baseUrl = xpmBaseByEnv[envName] ?? 'https://sso-xpm-test.dashboardinsights.bento.solutions'
  }
  if (type === 'BRWFM') {
    baseUrl = getBrwfmRedirectUrl(envName)
  }
  if (type === 'MYOB') {
    baseUrl = `https://secure.myob.com/oauth2/account/authorize?client_id=${myobClientId}&redirect_uri=${origin}/myob&response_type=code&scope=sme-company-file%20sme-general-ledger%20sme-sales%20sme-timebilling%20sme-inventory%20sme-contacts-customer%20sme-contacts-personal%20sme-contacts-employee%20sme-contacts-supplier%20sme-banking%20sme-purchases%20sme-payroll&prompt=consent`
  }
  if (type === 'QuickBooks') {
    baseUrl = `https://appcenter.intuit.com/connect/oauth2?client_id=${quickBooksClientId}&response_type=code&scope=com.intuit.quickbooks.accounting&redirect_uri=${origin}/quickbooks`
  }

  if (!baseUrl) return ''
  if (type === 'MYOB') return oauthState ? `${baseUrl}&state=${encodeURIComponent(oauthState)}` : ''
  if (type === 'QuickBooks') return `${baseUrl}&state=${platformId}`
  if (type !== 'BRWFM') return `${baseUrl}?platformId=${platformId}`
  return baseUrl
}

export function isManualRefreshAllowed(row: PlatformConnectionRow) {
  return row.type === 'Xero' && row.status !== 'Refreshing'
}

export function isDisconnectAllowed(row: PlatformConnectionRow) {
  if (row.status === 'Refreshing') return false

  const hasConnectionMaterial =
    row.isAuthenticated ||
    !!row.hasRefreshToken ||
    !!row.hasApiKey ||
    !!row.hasApiSecret ||
    !!row.hasOtherCredentials ||
    !!row.apiKey ||
    !!row.apiSecret ||
    !!row.username ||
    !!row.password ||
    !!row.account ||
    !!row.warehouse

  const hasLegacyPendingCompany = row.status === 'Pending' && !!row.companyName.trim()

  return hasConnectionMaterial || hasLegacyPendingCompany
}

export function requiresApiCredentials(type: PlatformType) {
  return type === 'WorkGuru' || type === 'FYI'
}

export function requiresKarbonCredentials(type: PlatformType) {
  return type === 'Karbon'
}

export function redactedValue(value?: string) {
  if (!value) return 'Not set'
  if (value.length <= 5) return value
  return `${value.slice(0, 5)}${'*'.repeat(value.length - 5)}`
}

export type CredentialValues = {
  type: PlatformType
  apiKey?: string
  apiSecret?: string
  username?: string
  password?: string
  account?: string
  warehouse?: string
}

export function clearCredentialFields(nextType: PlatformType): CredentialValues {
  return {
    type: nextType,
    apiKey: undefined,
    apiSecret: undefined,
    username: undefined,
    password: undefined,
    account: undefined,
    warehouse: undefined,
  }
}

export function validatePlatformCredentials(values: CredentialValues) {
  if (requiresApiCredentials(values.type) && (!values.apiKey || !values.apiSecret)) {
    return 'API Key and API Secret are required for this platform type'
  }
  if (
    requiresKarbonCredentials(values.type) &&
    (!values.username || !values.password || !values.account || !values.warehouse)
  ) {
    return 'Karbon requires username, password, account, and warehouse'
  }
  return ''
}

export function openPopup(url: string, width = 600, height = 500) {
  const leftPosition = window.screen.width / 2 - (width / 2 + 10)
  const topPosition = window.screen.height / 2 - (height / 2 + 50)
  return window.open(
    url,
    'platform_auth_popup',
    `status=no,height=${height},width=${width},resizable=yes,left=${leftPosition},top=${topPosition},screenX=${leftPosition},screenY=${topPosition},toolbar=no,menubar=no,location=no,directories=no`
  )
}
